import { Autocomplete } from '@mui/material';
import { FC, memo, SetStateAction, useCallback, useMemo } from 'react';

import { StyledTextField } from 'components/styled';
import { Booking, Chassis, Customer, EquipmentType, Genset, UnitStatus } from 'types';

import { SearchableSelect, SearchableSelectOption } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import { useNewTransaction } from '..';
import { AccountSearch } from '../account';
import { BookingInput } from '../booking';
import { InboundUnitSearch, OutboundUnitSearch } from '../equipment';
import ManufacturedDate from '../equipment/manufactured-date';

const isNonEmptyGensetStack = (stack: Chassis[] | Genset[]): stack is Genset[] =>
  !!stack.length && stack[0] instanceof Genset;

/* memoize change handler to avoid rerendering account field / repeated valiidation calls */
const GensetAccount: FC<{ index: number }> = memo(({ index }) => {
  const { stack, setStack } = useNewTransaction();
  const indexGenset: Genset = useMemo(() => stack[index] as Genset, [stack, index]);
  const setCustomer = useCallback(
    (customer?: Customer | null) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        const [current] = stackCopy.splice(index, 1);
        stackCopy.splice(index, 0, new Genset({ ...current, customer }));
        return stackCopy as typeof readOnlyStack;
      });
    },
    [index, setStack]
  );
  return (
    <AccountSearch
      required
      unit={indexGenset}
      setCustomer={setCustomer}
      data-cy={`mounted-genset-${index}-account-search`}
    />
  );
});

const GensetBooking: FC<{ index: number }> = memo(({ index }) => {
  const { stack, transaction, setStack } = useNewTransaction();
  const indexGenset: Genset = useMemo(() => stack[index] as Genset, [stack, index]);
  const setBooking = useCallback(
    (args?: SetStateAction<Partial<Booking> | undefined | null>) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        if (index in stackCopy) {
          stackCopy[index].booking = typeof args === 'function' ? args(stackCopy[index].booking) : args;
        }
        return stackCopy as typeof readOnlyStack;
      });
    },
    [index, setStack]
  );
  return (
    <BookingInput
      unit={indexGenset}
      setBooking={setBooking}
      required={!transaction.inbound}
      data-cy={`mounted-genset-${index}-booking-search`}
    />
  );
});

const GensetSection: FC = () => {
  const { chassis, stack, transaction, setChassis, setStack } = useNewTransaction();
  const { unitStatusOptions } = useLookups();
  const { gensetSizeTypeEntries } = useSizeTypes();

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return gensetSizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [gensetSizeTypeEntries]);

  const deleteGenset = useCallback(
    (index: number) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        stackCopy.splice(index, 1);
        return stackCopy as typeof readOnlyStack;
      });
    },
    [setStack]
  );

  const setGenset = useCallback(
    (index: number, args?: SetStateAction<Genset>) => {
      setStack(readOnlyStack => {
        const stackCopy = [...readOnlyStack];
        if (index in stackCopy) {
          stackCopy[index] = new Genset(typeof args === 'function' ? args(stackCopy[index] as any) : args);
        }
        return stackCopy as typeof readOnlyStack;
      });
    },
    [setStack]
  );

  const validateOutboundStackedGenset = useCallback(
    async (unit: Genset | null) => {
      if (!unit) {
        return;
      }
      if (unit.baseUnit) {
        if (!chassis.id) {
          setChassis(new Chassis(unit.baseUnit));
        } else if (chassis.id !== unit.baseUnit.id) {
          return `Base chassis mismatch. ${unit.unitNumber} already mounted on ${unit.baseUnit.unitNumber}`;
        }
      }
    },
    [chassis, setChassis]
  );

  if (!isNonEmptyGensetStack(stack)) return null;

  return (
    <>
      {stack.map((g, i) => (
        <div key={g.id}>
          <hr className="mb-4" />
          <div className="grid-3">
            {transaction.inbound ? (
              <InboundUnitSearch
                equipmentType={EquipmentType.GENSET}
                required
                setUnit={args => setGenset(i, args)}
                unit={g}
                data-cy={`mounted-genset-${i}-inbound-unit-number-search`}
              />
            ) : (
              <OutboundUnitSearch
                required
                setUnit={args => setGenset(i, args)}
                unit={g}
                customValidation={validateOutboundStackedGenset}
                data-cy={`mounted-genset-${i}-outbound-unit-number-search`}
              />
            )}
            <div>
              <SearchableSelect
                containerClass="text-field"
                inputClass={!g.sizeType ? 'blank' : ''}
                id={`genset-${g.id}-size-type`}
                label="SIZE TYPE"
                disabled={!transaction.inbound}
                required
                onClear={() => setGenset(i, g => ({ ...g, sizeType: undefined }))}
                onValueSelected={(sizeType: any) => setGenset(i, g => ({ ...g, sizeType }))}
                options={sizeTypeOptions}
                value={g.sizeType ?? ''}
                data-cy={`mounted-genset-${i}-size-type-input`}
              />
            </div>
            <div>
              <GensetAccount key={i} index={i} />
            </div>
            <div>
              <Autocomplete<UnitStatus | ''>
                fullWidth
                autoHighlight
                size="small"
                disabled={!transaction.inbound}
                options={['', ...unitStatusOptions]}
                value={g.unitStatus ?? ''}
                onChange={(_, status) => setGenset(i, g => ({ ...g, unitStatus: status || undefined }))}
                getOptionLabel={status => status ?? ''}
                renderInput={params => (
                  <StyledTextField
                    {...params}
                    label="STATUS"
                    required
                    data-cy={`mounted-genset-${i}-unit-status-input`}
                  />
                )}
                renderOption={(props, status) => (
                  <li {...props} key={status ?? ''}>
                    {status ?? ''}
                  </li>
                )}
              />
            </div>
            <div>
              <GensetBooking key={i} index={i} />
            </div>
            <ManufacturedDate unit={g} setUnit={u => setGenset(i, u)} />
            <div className="col-start-3 d-flex g-2 h-100">
              <div className="flex-fill d-flex g-2 h-100 align-items-center justify-content-center">
                <label className="font-weight-bold">CARB COMPLIANT</label>
                <input
                  type="checkbox"
                  checked={!!g.carbCompliant}
                  disabled={!transaction.inbound}
                  onChange={e => setGenset(i, g => ({ ...g, carbCompliant: e.target.checked }))}
                  data-cy={`mounted-genset-${i}-carb-compliant-checkbox`}
                />
              </div>
              <div className="d-flex h-100 align-items-center">
                <button
                  className="btn-close delete"
                  type="button"
                  onClick={() => deleteGenset(i)}
                  data-cy={`mounted-genset-${i}-delete-button`}
                >
                  <span className="material-icons icon-xlarge">delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default GensetSection;
